import {
  type AcpPlanData,
  type AcpSubagentData,
  type CrowdexUIMessage,
  toolImageCallId,
} from '@crowdex/agent-contracts';
import type { PromptInputMessage } from '@crowdex/ui/ai/prompt-input';
import { readableReferences } from '@crowdex/ui/lib/inline-reference';
import type { AgentTerminalDisplay } from '@/components/desktop-panel-primitives';
import { backgroundOutputFile } from '@/components/desktop-side-panel';
import {
  type BackgroundTerminalCompletion,
  backgroundOutputCompletion,
  backgroundTerminalRunning,
  terminalRunning,
} from '@/lib/conversations/message-part/terminal';
import type { DynamicToolPart } from '@/lib/conversations/message-part/types';
import { PREVIEW_THREADS } from '@/lib/desktop-preview-data';
import { previewBoardTitle } from '@/lib/desktop-preview-transcript';
import { firstUserMessageText } from './conversation-title-sync';

/**
 * Pure derivations of conversation state from the flat message list. These were
 * inline `useMemo` bodies inside `DesktopShell`; extracting them keeps the shell
 * a thin orchestrator and lets the branchy scans be unit tested in isolation.
 * All scans walk messages/parts back-to-front to prefer the most recent value.
 */

/** The plan to display: the live submitted plan wins, else the latest plan found in messages. */
export function resolveDisplayedPlan(messages: readonly CrowdexUIMessage[]): AcpPlanData | null {
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const message = messages[messageIndex];
    if (message === undefined) continue;
    for (let partIndex = message.parts.length - 1; partIndex >= 0; partIndex -= 1) {
      const part = message.parts[partIndex];
      if (part?.type === 'data-plan') return part.data;
    }
  }
  return null;
}

/** Subagent data matching the selected tool call id, or null when none is selected/found. */
export function selectedSubagentData(
  messages: readonly CrowdexUIMessage[],
  selectedSubagentId: string | null,
): AcpSubagentData | null {
  if (selectedSubagentId === null) return null;
  for (const message of messages) {
    const part = message.parts.find(
      (candidate) =>
        candidate.type === 'data-subagent' && candidate.data.toolCallId === selectedSubagentId,
    );
    if (part?.type === 'data-subagent') return part.data;
  }
  return null;
}

/** All subagents referenced anywhere in the conversation, de-duplicated by tool call id. */
export function conversationSubagents(
  messages: readonly CrowdexUIMessage[],
): readonly AcpSubagentData[] {
  const subagents = new Map<string, AcpSubagentData>();
  for (const message of messages) {
    for (const part of message.parts) {
      if (part.type === 'data-subagent') subagents.set(part.data.toolCallId, part.data);
    }
  }
  return [...subagents.values()];
}

/** What a tool call ran: its `command` input, else its title or name. */
function commandText(part: DynamicToolPart): string {
  const input =
    part.input !== null && typeof part.input === 'object' && !Array.isArray(part.input)
      ? (part.input as Record<string, unknown>)
      : null;
  const command = input !== null && typeof input['command'] === 'string' ? input['command'] : null;
  return command ?? part.title ?? part.toolName;
}

/**
 * The agent's commands, each as the terminal its step opens: from its `data-terminal` parts,
 * which every agent writes for its commands, and their tool call.
 */
export function messageAgentTerminals(
  messages: readonly CrowdexUIMessage[],
): readonly AgentTerminalDisplay[] {
  const calls = new Map<string, string>();
  const states = new Map<string, DynamicToolPart['state']>();
  const terminals = new Map<string, AgentTerminalDisplay>();
  // Terminal references and tool completion updates can arrive in either order.
  for (const message of messages) {
    for (const part of message.parts) {
      if (part.type === 'dynamic-tool') {
        calls.set(part.toolCallId, commandText(part));
        states.set(part.toolCallId, part.state);
      }
    }
  }
  for (const message of messages) {
    for (const part of message.parts) {
      if (part.type === 'data-terminal') {
        const call = calls.get(part.data.toolCallId);
        const outputFile = backgroundOutputFile(part.data.output);
        terminals.set(part.data.terminalId, {
          id: part.data.terminalId,
          command: call ?? 'Agent command',
          output: part.data.output,
          truncated: part.data.truncated,
          exitCode: part.data.exitCode,
          signal: part.data.signal,
          running:
            outputFile !== null
              ? backgroundTerminalRunning(
                  part.data.exitCode,
                  part.data.signal,
                  true,
                  part.data.backgroundCompleted,
                )
              : terminalRunning(
                  part.data.exitCode,
                  part.data.signal,
                  states.get(part.data.toolCallId),
                ),
          backgroundCompleted: part.data.backgroundCompleted,
          ...(part.data.cwd === undefined ? {} : { cwd: part.data.cwd }),
          ...(part.data.durationMs === undefined ? {} : { durationMs: part.data.durationMs }),
          ...(states.get(part.data.toolCallId) === undefined
            ? {}
            : { toolState: states.get(part.data.toolCallId) }),
          ...(outputFile === null ? {} : { outputFile }),
        });
      }
    }
  }
  return [...terminals.values()];
}

/** Mark a restored transcript terminal as stopped when no live connection entry exists. */
export function stopAgentTerminalParts(
  messages: readonly CrowdexUIMessage[],
  terminalId: string,
  completion: BackgroundTerminalCompletion = { exitCode: null, signal: 'killed' },
): CrowdexUIMessage[] {
  let changed = false;
  const next = messages.map((message) => {
    let messageChanged = false;
    const parts = message.parts.map((part) => {
      if (
        part.type !== 'data-terminal' ||
        part.data.terminalId !== terminalId ||
        (part.data.backgroundCompleted === true &&
          part.data.exitCode === completion.exitCode &&
          part.data.signal === completion.signal)
      ) {
        return part;
      }
      changed = true;
      messageChanged = true;
      return {
        ...part,
        data: {
          ...part.data,
          exitCode: completion.exitCode,
          signal: completion.signal,
          backgroundCompleted: true,
        },
      };
    });
    return messageChanged ? { ...message, parts } : message;
  });
  return changed ? next : (messages as CrowdexUIMessage[]);
}

/** Reconcile restored background tasks against their durable output files. */
export async function completedBackgroundTerminals(
  terminals: readonly AgentTerminalDisplay[],
  readOutput: (outputFile: string) => Promise<string>,
): Promise<Array<{ id: string; completion: BackgroundTerminalCompletion }>> {
  const stopped = await Promise.all(
    terminals.map(async (terminal) => {
      if (
        terminal.outputFile === undefined ||
        terminal.signal !== null ||
        terminal.backgroundCompleted === true
      )
        return null;
      try {
        const completion = backgroundOutputCompletion(await readOutput(terminal.outputFile));
        return completion === null ? null : { id: terminal.id, completion };
      } catch {
        return null;
      }
    }),
  );
  return stopped.filter(
    (item): item is { id: string; completion: BackgroundTerminalCompletion } => item !== null,
  );
}

/** File-part sources across the conversation, as `{ label, url }` link descriptors. */
export function conversationSources(
  messages: readonly CrowdexUIMessage[],
): readonly { label: string; url: string }[] {
  const sources = new Map<string, { label: string; url: string }>();
  for (const message of messages) {
    for (const part of message.parts) {
      // A tool's image is shown where its step ran; its data URL is not a source to link.
      if (part.type === 'file' && toolImageCallId(part) === null && !sources.has(part.url)) {
        sources.set(part.url, { label: part.filename ?? part.mediaType, url: part.url });
      }
    }
  }
  return [...sources.values()];
}

/** Most recent usage payload emitted by the agent, or null when none exists yet. */
export function latestUsage(messages: readonly CrowdexUIMessage[]) {
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const message = messages[messageIndex];
    if (message?.metadata?.usage !== undefined) return message.metadata.usage;
    const parts = message?.parts ?? [];
    for (let partIndex = parts.length - 1; partIndex >= 0; partIndex -= 1) {
      const part = parts[partIndex];
      if (part?.type === 'data-usage') return part.data;
    }
  }
  return null;
}

/** Provisional title derived from the first user message (used in preview mode). */
export function previewConversationTitle(
  messages: readonly CrowdexUIMessage[],
  /** A preview opened on one of its conversations (a project's task) says its title. */
  chatId?: string,
): string {
  const listed = PREVIEW_THREADS.find((thread) => thread.id === chatId)?.title;
  if (listed !== undefined) return listed;
  // A board's conversation is named as on the board (a child by its own name, D49).
  const board = previewBoardTitle();
  if (board !== null) return board;
  const message = firstUserMessageText(messages);
  if (!message) return 'New conversation';
  const text = readableReferences(message, 'plain');
  return text.length > 72 ? `${text.slice(0, 72).trimEnd()}…` : text;
}

/** Optimistic user message rendered while a cloud runtime provisions, or null when idle. */
export function pendingCloudUiMessage(
  chatId: string,
  pendingCloudMessage: PromptInputMessage | null,
): CrowdexUIMessage | null {
  if (pendingCloudMessage === null) return null;
  return {
    id: `pending-cloud-${chatId}`,
    role: 'user',
    parts: [
      ...(pendingCloudMessage.text.length > 0
        ? [{ type: 'text' as const, text: pendingCloudMessage.text }]
        : []),
      ...pendingCloudMessage.files,
    ],
  };
}
